/**
 * Parse v1/v2 proposal markdown for staff names per position_id / role_title.
 * Mirrors STAFF SECTION DATA blocks from report_generator.py (~ASSIGNED: lines).
 */

export interface ParsedStaffAssignment {
  name: string;
  rawLine?: string;
  qualSnippet?: string;
}

export interface StaffRequirementRef {
  position_id: string;
  role_title?: string;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function cleanAssignedName(raw: string): string | null {
  const line = raw.trim();
  if (!line || line.startsWith('(no')) return null;
  const name = line.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return name && !name.startsWith('(no') ? name : null;
}

function extractQualSnippet(block: string): string | undefined {
  const creds = block.match(/^\s*(?:Credentials|Designations):\s*(.+)$/im);
  if (creds?.[1]) return creds[1].trim();
  const dept = block.match(/^\s*Department:\s*(.+)$/im);
  if (dept?.[1]) return dept[1].trim();
  return undefined;
}

function normalizeRole(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findRoleTitleKey(
  roleTitle: string,
  requirements: StaffRequirementRef[],
): string | undefined {
  const norm = normalizeRole(roleTitle);
  const exact = requirements.find((r) => r.role_title && normalizeRole(r.role_title) === norm);
  if (exact) return exact.position_id;
  const partial = requirements.find((r) => {
    const rt = r.role_title ? normalizeRole(r.role_title) : '';
    return rt && (rt.includes(norm) || norm.includes(rt));
  });
  return partial?.position_id;
}

/**
 * Parse proposal markdown for assigned staff per position_id.
 */
export function parseProposalStaffAssignments(
  content: string,
  staffRequirements: StaffRequirementRef[],
): Record<string, ParsedStaffAssignment> {
  const result: Record<string, ParsedStaffAssignment> = {};
  if (!content?.trim() || staffRequirements.length === 0) return result;

  // STAFF SECTION DATA: **Role: X** (position_id: Y) … ASSIGNED: Name
  const roleBlockRegex =
    /\*\*Role:\s*([^*]+)\*\*\s*\(position_id:\s*([^)]+)\)([\s\S]*?)(?=\n\*\*Role:|\n##\s|$)/gi;
  let match: RegExpExecArray | null;
  while ((match = roleBlockRegex.exec(content)) !== null) {
    const positionId = match[2].trim();
    const block = match[3];
    const assignedMatch = block.match(/^\s*ASSIGNED:\s*(.+)$/im);
    if (!assignedMatch) continue;
    const rawLine = assignedMatch[1].trim();
    const name = cleanAssignedName(rawLine);
    if (name) {
      result[positionId] = { name, rawLine, qualSnippet: extractQualSnippet(block) };
    }
  }

  // Standalone ASSIGNED lines near position_id mentions
  for (const req of staffRequirements) {
    if (result[req.position_id]) continue;
    const pid = escapeRegex(req.position_id);
    const nearPid = new RegExp(
      `position_id:\\s*${pid}[\\s\\S]{0,400}?ASSIGNED:\\s*(.+)$`,
      'im',
    );
    const m = content.match(nearPid);
    if (m) {
      const rawLine = m[1].trim();
      const name = cleanAssignedName(rawLine);
      if (name) result[req.position_id] = { name, rawLine };
    }
  }

  // ### Role Title sections (v2 executive summary staff subsections)
  for (const req of staffRequirements) {
    if (result[req.position_id]) continue;
    const roleTitle = req.role_title?.trim();
    if (!roleTitle) continue;

    const headingRegex = new RegExp(
      `(?:^|\\n)#{2,3}\\s+${escapeRegex(roleTitle)}\\s*\\n([\\s\\S]*?)(?=\\n#{2,3}\\s|$)`,
      'i',
    );
    const sectionMatch = content.match(headingRegex);
    if (!sectionMatch) continue;

    const body = sectionMatch[1];
    const assignedInBody = body.match(/ASSIGNED:\s*(.+)/i);
    if (assignedInBody) {
      const rawLine = assignedInBody[1].trim();
      const name = cleanAssignedName(rawLine);
      if (name) {
        result[req.position_id] = { name, rawLine, qualSnippet: extractQualSnippet(body) };
        continue;
      }
    }

    const namePatterns = [
      /\*\*([^*]{3,80})\*\*/,
      /(?:^|\n)\s*(?:Name|Assigned|Proposed)(?:\s+staff)?:\s*\*?\*?([^*\n]{3,80})/i,
      /(?:^|\n)\s*([A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/,
    ];
    for (const pat of namePatterns) {
      const nm = body.match(pat);
      if (nm?.[1]) {
        const candidate = nm[1].trim().replace(/[,.]$/, '');
        if (candidate.length > 3 && !/^(the|our|this|that)\b/i.test(candidate)) {
          result[req.position_id] = { name: candidate, qualSnippet: extractQualSnippet(body) };
          break;
        }
      }
    }
  }

  // Map ### headings that differ slightly from role_title
  const headingRegex = /(?:^|\n)(#{2,3})\s+(.+?)\s*\n([\s\S]*?)(?=\n#{2,3}\s|$)/g;
  while ((match = headingRegex.exec(content)) !== null) {
    const heading = match[2].trim();
    const body = match[3];
    const positionId = findRoleTitleKey(heading, staffRequirements);
    if (!positionId || result[positionId]) continue;

    const assignedInBody = body.match(/ASSIGNED:\s*(.+)/i);
    if (assignedInBody) {
      const rawLine = assignedInBody[1].trim();
      const name = cleanAssignedName(rawLine);
      if (name) {
        result[positionId] = { name, rawLine, qualSnippet: extractQualSnippet(body) };
        continue;
      }
    }

    const bold = body.match(/\*\*([^*]{3,80})\*\*/);
    if (bold?.[1]) {
      result[positionId] = { name: bold[1].trim(), qualSnippet: extractQualSnippet(body) };
    }
  }

  return result;
}

/**
 * Merge v2 (preferred) and v1 parsed assignments.
 */
export function mergeProposalStaffAssignments(
  v2: Record<string, ParsedStaffAssignment>,
  v1: Record<string, ParsedStaffAssignment>,
): Record<string, ParsedStaffAssignment> {
  return { ...v1, ...v2 };
}
